import {
  setAccessToken,
  setCookie,
  BackrsFormField,
  BackrsFormValues,
  BackrsUser,
  CareerCategory,
  HIGH_SCHOOL_START_TERM,
} from '@backrs_ui/common-ui';

import {
  usePostProfileOptionAnswerMutation,
  usePostProfileOptionMutation,
  usePostUserMutation,
} from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';
import { useTrackUserActivityMutation } from '@backrs_ui/grow-ui/src/redux/user/user.api';
import { ENVIRONMENT } from '@backrs_ui/grow-ui/src/settings';

import { OnboardingQuestion } from '../onboarding.types';
import { HIGH_SCHOOL_ENROLLMENT_TYPE } from '../questions/questions.unified';
import { extractUserDataFromForm } from '../utils/extractUserDataFields';
import {
  formatProfileOption,
  getProfileOptionKeysByQuestionId,
} from '../utils/formatFormQuestions';

type UsePostDataProps = Readonly<{
  questions?: Record<string, OnboardingQuestion>;
  careerPathCategories: CareerCategory[];
}>;

export const usePostData = ({ questions, careerPathCategories }: UsePostDataProps) => {
  const [postUser] = usePostUserMutation();
  const [postProfileOptionAnswer] = usePostProfileOptionAnswerMutation();
  const [postProfileOption] = usePostProfileOptionMutation();
  const [postUserActivity] = useTrackUserActivityMutation();

  const handleCreateUser = async (formData: BackrsFormValues): Promise<BackrsUser> => {
    const userData = extractUserDataFromForm(formData);

    const { user } = await postUser({
      userData: userData as BackrsFormValues,
    }).unwrap();

    // Set the authentication token so subscription API calls work
    if (user.access_token) {
      setAccessToken('backrs_ui_token', user.access_token);
      setCookie(user.access_token, ENVIRONMENT);
    }

    return user;
  };

  const handlePostProfileOptionAnswers = async (formData: BackrsFormValues, user: BackrsUser) => {
    const profileOptionKeysByQuestionId = getProfileOptionKeysByQuestionId({
      formData,
      userId: user.id,
      questions,
      careerPathCategories,
    });

    for (const [questionId, profileOptionAnswers] of Object.entries(
      profileOptionKeysByQuestionId,
    )) {
      await postProfileOptionAnswer({
        profileOptionAnswers,
        postKey: questionId,
      }).unwrap();
    }
  };

  // now we can update the user activity endpoint
  const handlePostUserActivity = async () => {
    await postUserActivity(undefined).unwrap();
  };

  /* this function is used to post to the new user profile update endpoint. Long term all profile options will be posted here */
  const handlePostProfileOption = async (
    formData: BackrsFormValues,
    questions?: Record<string, OnboardingQuestion>,
  ) => {
    if (!questions) {
      return;
    }

    const profileOptionQuestions: BackrsFormField[] = formatProfileOption({ questions }) || [];

    const onlyFilledProfileOptions = profileOptionQuestions?.filter(Boolean);

    if (!onlyFilledProfileOptions) {
      return;
    }

    // Build a single object with all profile option fields
    const profileOptionData: Record<string, string | number> = {};

    for (const userFormData of Object.keys(formData)) {
      const profileOption = onlyFilledProfileOptions.find((option) => option.name === userFormData);

      if (!profileOption) continue;

      const value = formData[userFormData];

      if (value !== undefined && value !== null && value !== '') {
        profileOptionData[userFormData] = value as string | number;
      }
    }

    // Transform generic field names to API-specific field names based on enrollment type
    const transformProfileOptionData = (
      data: Record<string, string | number>,
    ): Record<string, string | number> => {
      const enrollmentType = data.academicEnrollmentTypes;
      const isHighSchool = enrollmentType === HIGH_SCHOOL_ENROLLMENT_TYPE;
      const isNotStudent = enrollmentType === '6';

      const transformedData: Record<string, string | number> = {};

      for (const [key, value] of Object.entries(data)) {
        if (key === 'academicExpectedGraduationYear') {
          // Skip if "Not currently a student"
          if (isNotStudent) continue;

          // Map to correct graduation year field
          const apiKey = isHighSchool
            ? 'academicHighSchoolExpectedGraduationYear'
            : 'academicPostSecondaryExpectedGraduationYear';

          transformedData[apiKey] = value;
        } else if (key === 'academicsCurrentEnrollment') {
          // Skip if "Not currently a student"
          if (isNotStudent) continue;

          // Map to correct current enrollment field
          const apiKey = isHighSchool
            ? 'academicsHighSchoolCurrentEnrollment'
            : 'academicsPostSecondaryCurrentEnrollment';

          transformedData[apiKey] = value;
        } else if (key === 'academicProgramStartTerm') {
          if (isNotStudent || isHighSchool) continue;

          transformedData.academicPostSecondaryProgramStartTerm = value;
        } else if (key === 'academicProgramStartYear') {
          if (isNotStudent) continue;

          if (isHighSchool) {
            transformedData.academicHighSchoolProgramStartYear = value;
            transformedData.academicHighSchoolProgramStartTerm = HIGH_SCHOOL_START_TERM;
          } else {
            transformedData.academicPostSecondaryProgramStartYear = value;
          }
        } else {
          transformedData[key] = value;
        }
      }

      return transformedData;
    };

    // Make a single PATCH request with all fields (using transformed data)
    const transformedProfileOptionData = transformProfileOptionData(profileOptionData);

    if (Object.keys(transformedProfileOptionData).length > 0) {
      await postProfileOption({
        profileOptionAnswers: transformedProfileOptionData,
        postKey: 'profile_options_batch',
      }).unwrap();
    }
  };

  return {
    handleCreateUser,
    handlePostProfileOptionAnswers,
    handlePostUserActivity,
    handlePostProfileOption,
  };
};
